/* eslint-disable @typescript-eslint/no-unused-vars */
/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import React, { FC, ReactNode, useMemo, useState } from "react";
import { Button } from "react-bootstrap";
import { ImagesViewerModalProps } from "./ViewerModal";
import { ViewerButtonType } from "@advapacs/common/src/constant";
import DownloadWhiteIcon from "@src/assets/icons/download-white.svg";
import ShareIcon from "@src/assets/icons/share_1.svg";
import PlusIcon from "@src/assets/icons/plus-icon.svg";
import MinusIcon from "@src/assets/icons/minus-icon.svg";
import OneIcon from "@src/assets/icons/1-1-icon.svg";
import RedoIcon from "@src/assets/icons/redo-alt.svg";
import ArrowAltHIcon from "@src/assets/icons/arrows-alt-h.svg";
import ArrowAltVIcon from "@src/assets/icons/arrows-alt-v.svg";
import ReplyFillLeft from "@src/assets/icons/reply-fill-left.svg";
import ReplyFillRight from "@src/assets/icons/reply-fill-right.svg";
import { StudyMultiLevelDropdown } from "@advapacs/common";
import { DownloadAsMenu } from "../DownloadAsMenu";
import { DownloadAsMenuData } from "@src/utils/downloadAs";

interface ViewerMobileFooterProps {
  isMobileOnly?: boolean;
  isTabletOnly?: boolean;
  expandBodyClass?: string;
  loading?: boolean;
  imgLoading?: boolean;
  indexSeriesImage?: number;
  seriesImagesViewerModal?: Array<ImagesViewerModalProps>;
  indexInstanceImage?: number;
  instanceImagesViewerModal?: Array<ImagesViewerModalProps>;
  handleIconClick: (type: ViewerButtonType) => void;
  downloadInstance?: () => void;
  downloadAsMenu?: DownloadAsMenuData;
  frameNavigator?: ReactNode;
  // Keep the stepper layout even without frames, so nothing shifts.
  reserveFrameNavigator?: boolean;
  shareImageInstance?: () => void;
}
/**
 * This component for mobile, tablet.
 * @param ViewerMobileFooter
 * @returns REACT.NODE
 */
export const ViewerMobileFooter: FC<ViewerMobileFooterProps> = ({
  isMobileOnly,
  isTabletOnly,
  loading,
  indexInstanceImage = 0,
  indexSeriesImage = -1,
  seriesImagesViewerModal,
  instanceImagesViewerModal,
  downloadInstance,
  downloadAsMenu,
  frameNavigator,
  reserveFrameNavigator,
  handleIconClick,
  expandBodyClass,
  shareImageInstance,
  imgLoading,
}) => {
  const [showGroupBtns, setShowGroupBtns] = useState<boolean>(false);
  const [showDownloadAs, setShowDownloadAs] = useState<boolean>(false);

  // Only one popover is open at a time: the More menu or Download As.
  const handleToggleMore = () => {
    setShowGroupBtns(!showGroupBtns);
    setShowDownloadAs(false);
  };
  const handleDownloadAsOpenChange = (nextOpen: boolean) => {
    setShowDownloadAs(nextOpen);
    if (nextOpen) setShowGroupBtns(false);
  };

  const showDeviceShareImageButton = navigator.canShare;

  const dropdownItems = [
    {
      name: "Flip Horizontal",
      iconImage: (
        <img className="m-r-xxs" src={ArrowAltHIcon} alt="Next Icon" />
      ),
      onHandleClick: () => handleIconClick(ViewerButtonType.FLIP_HORIZONTAL),
      show: true,
      disabled: false,
    },
    {
      name: "Flip Vertical",
      iconImage: (
        <img className="m-r-xxs" src={ArrowAltVIcon} alt="Next Icon" />
      ),
      onHandleClick: () => handleIconClick(ViewerButtonType.FLIP_VERTICAL),
      show: true,
      disabled: false,
    },
  ];

  return (
    <>
      <div
        className={`w-100 pos-r body-footer ${expandBodyClass} ${
          reserveFrameNavigator ? "has-frame-navigator" : ""
        }`}
      >
        {frameNavigator}
        <div className="series-navigator pp-series-navigator flex-1 m-b-xs">
          <Button
            disabled={loading}
            variant="light"
            onClick={() => handleIconClick(ViewerButtonType.ZOOM_IN)}
            className="nav-button "
          >
            <img className="padding-b-3px" src={PlusIcon} alt="Next Icon" />
          </Button>
          <Button
            disabled={loading}
            variant="light"
            onClick={() => handleIconClick(ViewerButtonType.ZOOM_OUT)}
            className="nav-button m-l-xxs"
          >
            <img className="padding-b-3px" src={MinusIcon} alt="Next Icon" />
          </Button>
          <Button
            disabled={loading}
            variant="light"
            onClick={() => handleIconClick(ViewerButtonType.ONT_TO_ONE)}
            className="nav-button m-l-xxs"
          >
            <img className="padding-b-3px" src={OneIcon} alt="Next Icon" />
          </Button>
          <Button
            disabled={loading}
            variant="light"
            onClick={() => handleIconClick(ViewerButtonType.RESET)}
            className="nav-button m-l-xxs"
          >
            <img className="padding-b-3px" src={RedoIcon} alt="Next Icon" />
          </Button>
          <Button
            disabled={loading}
            variant="light"
            onClick={() => handleIconClick(ViewerButtonType.ROTATE_LEFT)}
            className="nav-button m-l-xxs"
          >
            <img
              className="padding-b-3px"
              src={ReplyFillLeft}
              alt="Next Icon"
            />
          </Button>
          <Button
            disabled={loading}
            variant="light"
            onClick={() => handleIconClick(ViewerButtonType.ROTATE_RIGHT)}
            className="nav-button m-l-xxs"
          >
            <img
              className="padding-b-3px"
              src={ReplyFillRight}
              alt="Next Icon"
            />
          </Button>
          {isTabletOnly && (
            <>
              <Button
                disabled={loading}
                variant="light"
                onClick={() =>
                  handleIconClick(ViewerButtonType.FLIP_HORIZONTAL)
                }
                className="nav-button m-l-xxs"
              >
                <img
                  className="padding-b-3px"
                  src={ArrowAltHIcon}
                  alt="Next Icon"
                />
              </Button>
              <Button
                disabled={loading}
                variant="light"
                onClick={() => handleIconClick(ViewerButtonType.FLIP_VERTICAL)}
                className="nav-button m-l-xxs"
              >
                <img
                  className="padding-b-3px"
                  src={ArrowAltVIcon}
                  alt="Next Icon"
                />
              </Button>
            </>
          )}
          {isMobileOnly && (
            <>
              <StudyMultiLevelDropdown
                wrapperClass="m-l-xxs text-white"
                data={undefined}
                position="top"
                showSubItem
                size="sm"
                onClose={() => {
                  setShowGroupBtns(false);
                }}
                show={showGroupBtns}
                onHandleClick={handleToggleMore}
                items={dropdownItems}
              />
              {/* @ts-ignore */}
              {showDeviceShareImageButton && (
                <button
                  className="btn pp-btn-light m-l-xxs"
                  onClick={shareImageInstance}
                  disabled={imgLoading}
                >
                  <img
                    className="padding-b-3px"
                    src={ShareIcon}
                    alt="Share Icon"
                  />
                </button>
              )}
              {downloadAsMenu ? (
                <DownloadAsMenu
                  variant="viewer"
                  showChevron={false}
                  toggleClassName="btn btn-primary m-l-xxs"
                  open={showDownloadAs}
                  onOpenChange={handleDownloadAsOpenChange}
                  {...downloadAsMenu}
                />
              ) : (
                <button
                  className="btn btn-primary m-l-xxs"
                  onClick={() => {
                    downloadInstance?.();
                  }}
                >
                  <img
                    className="padding-b-3px"
                    src={DownloadWhiteIcon}
                    alt="Download White Icon"
                  />
                </button>
              )}
            </>
          )}
        </div>

        {isTabletOnly && (
          <div className="viewer__download justify-content-end">
            <>
              {/* @ts-ignore */}
              {showDeviceShareImageButton && (
                <button
                  className="btn pp-btn-light m-l-xxs"
                  onClick={shareImageInstance}
                  disabled={imgLoading}
                >
                  <img
                    className="padding-b-3px"
                    src={ShareIcon}
                    alt="Share Icon"
                  />
                </button>
              )}
              {downloadAsMenu ? (
                <DownloadAsMenu
                  variant="viewer"
                  toggleClassName="btn btn-primary m-l-xxs"
                  disabled={
                    !instanceImagesViewerModal ||
                    instanceImagesViewerModal.length <= 0
                  }
                  {...downloadAsMenu}
                />
              ) : (
                <button
                  className="btn btn-primary m-l-xxs"
                  onClick={() => {
                    downloadInstance?.();
                  }}
                  disabled={
                    !instanceImagesViewerModal ||
                    instanceImagesViewerModal.length <= 0
                  }
                >
                  <img
                    className="m-r-xxs padding-b-3px"
                    src={DownloadWhiteIcon}
                    alt="Download White Icon"
                  />
                </button>
              )}
            </>
          </div>
        )}
      </div>
    </>
  );
};
